import { useEffect, useRef, useState } from "react";
import { useUpdateEffect } from "react-use";

const createLocalStorageKey = (cacheKey: string) =>
  `selected-sites-of-${cacheKey}`;

type Parameters = {
  cacheKey: string;
};

type CachedSite = {
  id: string;
  label: string;
};

export const useAdminReportSiteCache = ({ cacheKey }: Parameters) => {
  const localStorageKey = createLocalStorageKey(cacheKey);
  const [selectedSites, setSelectedSites] = useState<CachedSite[]>([]);
  const initializedKey = useRef<string | null>(null);

  // 初回に localStorage から選択状態を復元する
  useEffect(() => {
    if (initializedKey.current === localStorageKey) return;

    if (typeof window === "undefined") {
      return;
    }

    try {
      const cache = window.localStorage.getItem(localStorageKey);
      if (!cache) {
        return;
      }

      const parsed = JSON.parse(cache);
      if (!Array.isArray(parsed)) {
        window.localStorage.removeItem(localStorageKey);
        return;
      }

      const filtered: CachedSite[] = [];
      for (const item of parsed) {
        if (
          item &&
          typeof item === "object" &&
          typeof (item as any).id === "string"
        ) {
          const id = (item as any).id as string;
          const label =
            typeof (item as any).label === "string" && (item as any).label
              ? ((item as any).label as string)
              : id;
          filtered.push({ id, label });
        }
      }

      if (filtered.length > 0) {
        setSelectedSites(filtered);
      } else {
        window.localStorage.removeItem(localStorageKey);
      }
    } catch {
      window.localStorage.removeItem(localStorageKey);
    } finally {
      initializedKey.current = localStorageKey;
    }
  }, [localStorageKey]);

  // no-op: availability filtering removed; we keep cached selections as-is

  // 選択変更時に localStorage へ保存する
  useUpdateEffect(() => {
    if (initializedKey.current !== localStorageKey) return;
    if (typeof window === "undefined") return;

    try {
      const jsonString = JSON.stringify(selectedSites);
      window.localStorage.setItem(localStorageKey, jsonString);
    } catch {
      window.localStorage.removeItem(localStorageKey);
    }
  }, [localStorageKey, selectedSites]);

  return {
    selectedSites,
    setSelectedSites,
    isInitialized: initializedKey.current === localStorageKey,
  };
};
